import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { getCenterAndZoom, hasWindow } from "./utils";

import { siteMetadata } from "../../../gatsby-config";

const Map = ({
  width,
  height,
  zoom,
  center,
  bounds,
  padding,
  styles,
  sources,
  layers,
  minZoom,
  maxZoom,
  onClick
}) => {
  const { mapboxToken } = siteMetadata;

  if (!mapboxToken) {
    console.error(
      `ERROR: Mapbox token is required in gatsby-config.js siteMetadata`
    );
  }

  // if there is no window, we cannot render this component
  if (!hasWindow) {
    return null;
  }

  // this ref holds the map DOM node so that we can pass it into Mapbox GL
  const mapNode = useRef(null);

  // this ref holds the map object once we have instantiated it, so that we
  // can use it in other hooks
  const mapRef = useRef(null);

  const hoverId = useRef(null);

  // construct the map within an effect that has no dependencies
  // this allows us to construct it only once at the time the
  // component is constructed.
  useEffect(() => {
    let mapCenter = center;
    let mapZoom = zoom;

    // If bounds are available, use these to establish center and zoom when map first loads
    if (bounds && bounds.length === 4) {
      const { center: boundsCenter, zoom: boundsZoom } = getCenterAndZoom(
        mapNode.current,
        bounds,
        padding
      );
      mapCenter = boundsCenter;
      mapZoom = boundsZoom;
    }

    // Token must be set before constructing map
    mapboxgl.accessToken = mapboxToken;

    const map = new mapboxgl.Map({
      container: mapNode.current,
      style: `mapbox://styles/vlandham/ck5n4sw3s0rn21io4j3y9e5ls`,
      center: mapCenter,
      zoom: mapZoom,
      minZoom,
      maxZoom
    });
    mapRef.current = map;
    window.map = map; // for easier debugging and querying via console

    map.scrollZoom.disable();
    map.addControl(new mapboxgl.NavigationControl(), `top-right`);

    map.on(`load`, () => {
      console.log(`map onload`);
      // add sources
      Object.entries(sources).forEach(([id, source]) => {
        map.addSource(id, source);
      });

      // add layers
      layers.forEach(layer => {
        map.addLayer(layer);
        map.on(`click`, layer.id, e => {
          if (onClick) {
            onClick(e);
          }
        });

        if (layer.type === `fill`) {
          map.on(`mouseenter`, layer.id, function() {
            map.getCanvas().style.cursor = `pointer`;
          });

          map.on(`mousemove`, layer.id, function(e) {
            if (e.features.length > 0) {
              if (hoverId.current) {
                Object.keys(sources).forEach(sourceId => {
                  map.setFeatureState(
                    { source: sourceId, id: hoverId.current },
                    { hover: false }
                  );
                });
              }

              hoverId.current = e.features[0].id;
              Object.keys(sources).forEach(sourceId => {
                map.setFeatureState(
                  { source: sourceId, id: hoverId.current },
                  { hover: true }
                );
              });
            }
          });

          // Change it back to a pointer when it leaves.
          map.on(`mouseleave`, layer.id, function(e) {
            map.getCanvas().style.cursor = ``;
            if (hoverId.current) {
              Object.keys(sources).forEach(sourceId => {
                map.setFeatureState(
                  { source: sourceId, id: hoverId.current },
                  { hover: false }
                );
              });
              hoverId.current = null;
            }
          });
        }
      });
    });

    // hook up map events here, such as click, mouseenter, mouseleave
    // e.g., map.on('click', (e) => {})
    // map.on("click", e => {
    //   if (onClick) {
    //     onClick(e);
    //   }
    // });

    // when this component is destroyed, remove the map
    return () => {
      map.remove();
    };
  }, []);

  // You can use other `useEffect` hooks to update the state of the map
  // based on incoming props.  Just beware that you might need to add additional
  // refs to share objects or state between hooks.

  const style = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: `100%`,
    height: height
  };

  return (
    <div className="relative clearfix py-4">
      <div ref={mapNode} style={style} />
    </div>
  );
};

Map.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  center: PropTypes.arrayOf(PropTypes.number),
  zoom: PropTypes.number,
  bounds: PropTypes.arrayOf(PropTypes.number),
  minZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  styles: PropTypes.arrayOf(PropTypes.string),
  padding: PropTypes.number,
  sources: PropTypes.object,
  layers: PropTypes.arrayOf(PropTypes.object)
};

Map.defaultProps = {
  width: `100%`,
  height: `500px`,
  center: [-122.332069, 47.60731],
  zoom: 13.5,
  bounds: null,
  minZoom: 12.5,
  maxZoom: 24,
  styles: [`light-v9`, `dark-v9`, `streets-v11`],
  padding: 0.1, // padding around bounds as a proportion
  sources: {},
  layers: [],
  onClick: null
};

export default Map;
